import { openDB, type IDBPDatabase } from 'idb';

/**
 * Persistent storage for epub.js generated "locations" data.
 *
 * Locations of a single large book can be hundreds of KB, and accumulating them
 * across every book a user opens blew the tight localStorage quota. We prefer
 * IndexedDB for its far larger budget, keyed by the book's storage key.
 *
 * There are corner cases where IndexedDB is not available, though — e.g. Safari
 * strips the API in Lockdown Mode and in some sandboxed/cross-origin iframe contexts,
 * where even referencing `indexedDB` throws a ReferenceError. When it is absent we
 * fall back to an in-memory cache: locations persist for the session and simply
 * regenerate on the next one. Locations are cheaply regenerable anyway, so this
 * degradation is safe.
 *
 * See: https://ilt-education.sentry.io/issues/128399048/
 */

const DB_NAME = 'book-player-locations';
const STORE_NAME = 'locations';

const isIndexedDbAvailable = (): boolean => {
  try {
    return typeof indexedDB !== 'undefined' && !!indexedDB;
  } catch {
    // `typeof` shouldn't throw, but accessing a stripped global can in some cases.
    return false;
  }
};

let dbPromise: Promise<IDBPDatabase> | null = null;

const getDb = (): Promise<IDBPDatabase> => {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, 1, {
      upgrade(db) {
        db.createObjectStore(STORE_NAME);
      },
    });
  }
  return dbPromise;
};

// In-memory fallback for realms without IndexedDB (e.g. Safari Lockdown Mode).
const memoryStore = new Map<string, string>();

export const getStoredLocations = async (key: string): Promise<string | undefined> => {
  if (!isIndexedDbAvailable()) {
    return memoryStore.get(key);
  }
  const db = await getDb();
  return db.get(STORE_NAME, key);
};

export const storeLocations = async (key: string, locations: string): Promise<void> => {
  if (!isIndexedDbAvailable()) {
    memoryStore.set(key, locations);
    return;
  }
  const db = await getDb();
  await db.put(STORE_NAME, locations, key);
};
